import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * 화면 설명 단추.
 *
 * ## 왜 화면마다 심지 않고 한 곳에서 띄우나
 * 설명을 화면 파일마다 넣으면 같은 말이 흩어지고, 어떤 화면에는 있고 어떤
 * 화면에는 없게 된다. 화면이 83개라 더 그렇다. 설명은 `lib/screenGuide.jsx`
 * 한 표에 모으고, 레이아웃이 현재 주소로 그 표를 찾아 **모든 화면의 같은
 * 자리**(경로 표시줄 오른쪽 끝)에 띄운다. 빠진 화면은 그 표를 보면 바로 보인다.
 *
 * ## 무엇을 쓰나
 * "이 버튼은 저장합니다" 같은 말은 쓰지 않는다 — 보면 안다. 화면만 봐서는
 * 알 수 없는 것을 쓴다. 여기서 넣은 값이 **어느 화면에 나타나는지**, 순서를
 * 틀리면 무엇이 막히는지, 지우면 되돌릴 수 있는지 같은 것들이다.
 */
export default function PageHelp({ title, children }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="이 화면 설명 보기"
        /*
          chrome-* 색은 **어두운 사이드바·상단바용**이다. 이 단추는 밝은
          경로 표시줄(bg-surface) 위에 있어서, 그 색을 쓰면 흐린 회색
          글씨가 된다. 여기서는 app-* 을 쓴다.
        */
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-bold text-app-muted transition-colors hover:bg-surface-hover hover:text-app-text"
      >
        <HelpCircle size={15} />
        <span className="hidden sm:inline">화면 설명</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 sm:items-center"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-app-border bg-surface px-6 py-4">
              <h3 className="flex items-center gap-2 text-base font-black text-app-text">
                <HelpCircle size={18} className="text-blue-600" />
                {title}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-app-muted transition-colors hover:bg-surface-hover"
                aria-label="닫기"
              >
                <X size={18} />
              </button>
            </div>
            {/*
              글자색은 text-app-text 다. text-app 이 아니다 —
              `text-app` 은 --color-app, 즉 **바탕색**(#f5f7fa)이라
              흰 모달 위에서 글씨가 거의 안 보인다. 실제로 그랬다.
            */}
            <div className="px-6 py-5 text-sm leading-relaxed text-app-text">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}

/** 설명 본문의 한 문단. */
export function HelpP({ children }) {
  return <p className="mb-3 leading-7">{children}</p>;
}

/** 설명 안의 작은 제목. */
export function HelpH({ children }) {
  return <p className="mb-1.5 mt-4 font-black text-app-text">{children}</p>;
}

/** 용어 → 뜻 목록. 「무엇이 무엇인지」를 늘어놓을 때 쓴다. */
export function HelpList({ items }) {
  return (
    <ul className="mb-3 list-disc space-y-1.5 pl-5 leading-7">
      {items.map((it) => (
        <li key={it.term}>
          <span className="font-bold text-app-text">{it.term}</span>
          {' — '}
          {it.desc}
        </li>
      ))}
    </ul>
  );
}

/** 다른 화면의 위치. 눈에 띄게 해서 「어디로 가라」가 분명해진다. */
export function HelpPath({ children }) {
  return (
    <code className="whitespace-nowrap rounded bg-surface-2 px-1.5 py-0.5 text-[0.85em] font-bold text-app-text">
      {children}
    </code>
  );
}
